import { api } from '@/services/backend/http-client';
import { DataTableFilterConfig, DataTableSortConfig } from '@opaz/ui-components';
import dayjs from 'dayjs';
import { getIsicDataByCode } from './isic';
import {
  ContractType,
  PageRequest,
  PageResponse,
  PermitType,
  PropertyListType,
  PropertyOwnershipCode,
  PropertyType,
  PropertyTypeCode,
  RegistryFilterRequestType,
  RegistryItemStatusCodeType,
  RegistryType,
  RegistryTypeCode,
  RentalContractType,
  ServiceProviderType,
  ZoneCodeType,
} from './types';

export const getRegistryList = async (
  registryTypeCode: RegistryTypeCode,
  pageRequest: PageRequest,
  sortConfig: DataTableSortConfig | null,
  filterConfig: DataTableFilterConfig | null,
  searchConfig: { [key in keyof RegistryFilterRequestType]?: RegistryFilterRequestType[key] },
): Promise<PageResponse<RegistryType>> => {
  const filterRequest: Partial<RegistryFilterRequestType> = { registryTypeCode, ...searchConfig };

  Object.entries(filterConfig || {}).forEach(([filterKey, filter]) => {
    if (filter.type === 'date-range') {
      if (filterKey === 'startDateRange') {
        if (filter.value.from) {
          filterRequest.startDateFrom = dayjs(filter.value.from).format('YYYY-MM-DD');
        }

        if (filter.value.to) {
          filterRequest.startDateTo = dayjs(filter.value.to).format('YYYY-MM-DD');
        }
      }

      if (filterKey === 'endDateRange') {
        if (filter.value.from) {
          filterRequest.endDateFrom = dayjs(filter.value.from).format('YYYY-MM-DD');
        }

        if (filter.value.to) {
          filterRequest.endDateTo = dayjs(filter.value.to).format('YYYY-MM-DD');
        }
      }
    } else if (filter.type === 'select') {
      if (filterKey === 'status') {
        filterRequest.statusCode = filter.value as RegistryItemStatusCodeType;
      } else if (filterKey === 'zone') {
        filterRequest.zoneCode = filter.value as ZoneCodeType;
      } else if (filterKey === 'registryCode') {
        filterRequest.registryCode = filter.value as string;
      }
    }
  });

  const response = await api.post<PageResponse<RegistryType>>(
    '/registry/records',
    {
      ...filterRequest,
    },
    { params: { ...pageRequest, ...(sortConfig && { sort: `${sortConfig.key},${sortConfig.direction}` }) } },
  );

  return response.data;
};

export type GetPropertiesFilterType = {
  size?: number;
  page?: number;
  zoneCode?: string;
  propertyTypeCode?: PropertyTypeCode;
  ownershipCode?: PropertyOwnershipCode;
  nameOrPlot?: string;
  ownerCrNumber?: string;
  sort?: string;
};

export const getProperties = async (filter: GetPropertiesFilterType = {}): Promise<PageResponse<PropertyListType>> => {
  const { zoneCode, propertyTypeCode, ownershipCode, nameOrPlot, ownerCrNumber, sort, ...queryParams } = filter;

  const { data } = await api.post<PageResponse<PropertyListType>>(
    '/registry/property/records/search',
    {
      zoneCode,
      propertyTypeCode,
      ownershipCode,
      nameOrPlot,
      ownerCrNumber,
    },
    {
      params: {
        ...queryParams,
        sort,
      },
    },
  );

  return data;
};

export const getRegistryItemDetails = async (
  registryNumber: string,
  version?: string,
): Promise<PermitType | ContractType | ServiceProviderType | PropertyType | RentalContractType> => {
  const response = await api.get<PermitType | ContractType | ServiceProviderType | PropertyType>(
    `/registry/records/${registryNumber}`,
    {
      params: version ? { version } : undefined,
    },
  );

  const registryItem = response.data;

  registryItem.attributes = await Promise.all(
    registryItem.attributes.map(async (attribute) => {
      if (['REG_LUCR_BA', 'REG_RECL_ISIC'].includes(attribute.code) && attribute.value) {
        const isicData = await getIsicDataByCode(attribute.value);

        return {
          ...attribute,
          valueType: 'TRANSLATED_STRING',
          value: JSON.stringify({ en: isicData.nameEn, ar: isicData.nameAr }),
        };
      }

      if (attribute.code === 'REG_REC_ISIC') {
        const codes = attribute.value
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean);
        const isicDataList = await Promise.all(codes.map((code) => getIsicDataByCode(code)));

        if (isicDataList.length > 0) {
          const formatName = (name: string, index: number) =>
            isicDataList.length > 1 ? `${index + 1}. ${name}` : name;

          return {
            ...attribute,
            valueType: 'TRANSLATED_STRING',
            value: JSON.stringify({
              en: isicDataList.map((d, index) => formatName(d.nameEn, index)).join('\n'),
              ar: isicDataList.map((d, index) => formatName(d.nameAr, index)).join('\n'),
            }),
          };
        }
      }

      return attribute;
    }),
  );

  return registryItem;
};

export const getRegistryDocument = async (registryNumber: string, documentNumber: string): Promise<Blob> => {
  const response = await api.get<Blob>(`/registry/records/${registryNumber}/document`, {
    params: documentNumber ? { documentNumber } : undefined,
    responseType: 'blob',
  });
  return response.data;
};

export const getRegistryCertificatePdf = async (recordNumber: string): Promise<Blob> => {
  const response = await api.get<Blob>(`/registry/records/${recordNumber}/certificate-pdf`, {
    responseType: 'blob',
  });
  return response.data;
};
